import { Geolocation, Position } from '@capacitor/geolocation';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';

export interface LocationData {
  latitude: number;
  longitude: number;
  accuracy?: number;
  heading?: number;
  speed?: number;
}

export class LocationTracker {
  private intervalId: NodeJS.Timeout | null = null;
  private isTracking = false;

  async requestPermission(): Promise<boolean> {
    try {
      console.log('LocationTracker: Requesting permission, platform:', Capacitor.getPlatform());
      
      // Check if we're running in a web environment
      if (Capacitor.getPlatform() === 'web') {
        // Use web geolocation API
        if (!navigator.geolocation) {
          console.error('LocationTracker: Geolocation not supported by browser');
          throw new Error('Geolocation is not supported by this browser');
        }
        // For web, we don't need explicit permission request - it's handled in getCurrentPosition/watchPosition
        console.log('LocationTracker: Web geolocation available');
        return true;
      } else {
        // Use Capacitor for mobile
        const permissionStatus = await Geolocation.requestPermissions();
        console.log('LocationTracker: Mobile permission status:', permissionStatus);
        return permissionStatus.location === 'granted';
      }
    } catch (error) {
      console.error('LocationTracker: Error requesting location permission:', error);
      return false;
    }
  }

  async getCurrentPosition(): Promise<LocationData | null> {
    try {
      if (Capacitor.getPlatform() === 'web') {
        // Use web geolocation API
        return new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              resolve({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                accuracy: position.coords.accuracy,
                heading: position.coords.heading || undefined,
                speed: position.coords.speed || undefined,
              });
            },
            (error) => {
              console.error('Web geolocation error:', error);
              reject(error);
            },
            {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 60000,
            }
          );
        });
      } else {
        // Use Capacitor for mobile
        const position = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000,
        });

        return {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          heading: position.coords.heading || undefined,
          speed: position.coords.speed || undefined,
        };
      }
    } catch (error) {
      console.error('Error getting current position:', error);
      return null;
    }
  }

  async startTracking(onLocationUpdate: (location: LocationData) => void): Promise<boolean> {
    try {
      if (this.isTracking) {
        console.log('LocationTracker: Already tracking location');
        return true;
      }

      console.log('LocationTracker: Starting location tracking (30-second intervals)...');
      const hasPermission = await this.requestPermission();
      if (!hasPermission) {
        console.error('LocationTracker: Permission denied');
        throw new Error('Location permission denied');
      }

      console.log('LocationTracker: Permission granted, setting up 30-second interval tracking...');

      // Get initial position
      const initialLocation = await this.getCurrentPosition();
      if (initialLocation) {
        console.log('LocationTracker: Initial location obtained:', {
          lat: initialLocation.latitude,
          lng: initialLocation.longitude,
          accuracy: initialLocation.accuracy
        });
        onLocationUpdate(initialLocation);
      }

      // Set up interval to get location every 30 seconds
      this.intervalId = setInterval(async () => {
        try {
          const location = await this.getCurrentPosition();
          if (location) {
            console.log('LocationTracker: Location update (30s interval):', {
              lat: location.latitude,
              lng: location.longitude,
              accuracy: location.accuracy
            });
            onLocationUpdate(location);
          }
        } catch (error) {
          console.error('LocationTracker: Error getting location in interval:', error);
        }
      }, 30000); // 30 seconds

      this.isTracking = true;
      console.log('Started location tracking with 30-second intervals');
      return true;
    } catch (error) {
      console.error('Error starting location tracking:', error);
      return false;
    }
  }

  async stopTracking(): Promise<void> {
    try {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }

      this.isTracking = false;
      console.log('Stopped location tracking');
    } catch (error) {
      console.error('Error stopping location tracking:', error);
    }
  }

  isCurrentlyTracking(): boolean {
    return this.isTracking;
  }
}

export const updateUserLocation = async (location: LocationData): Promise<void> => {
  try {
    console.log('LocationTracker: Updating user location in database...', location);
    
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) {
      console.error('LocationTracker: User not authenticated');
      throw new Error('Not authenticated');
    }

    const { data: profile } = await supabase
      .from('profiles')
      .select('id')
      .eq('user_id', user.user.id)
      .maybeSingle();

    if (!profile) {
      console.error('LocationTracker: Profile not found for user:', user.user.id);
      throw new Error('Profile not found');
    }

    console.log('LocationTracker: Found profile:', profile.id);

    // Check if location record exists
    const { data: existingLocation } = await supabase
      .from('user_locations')
      .select('id')
      .eq('user_id', user.user.id)
      .maybeSingle();

    if (existingLocation) {
      // Update existing location
      const { error } = await supabase
        .from('user_locations')
        .update({
          latitude: location.latitude,
          longitude: location.longitude,
          accuracy: location.accuracy,
          heading: location.heading,
          speed: location.speed,
          is_active: true,
          last_updated: new Date().toISOString(),
        })
        .eq('user_id', user.user.id);

      if (error) {
        console.error('LocationTracker: Database update error:', error);
        throw error;
      }
    } else {
      // Insert new location
      const { error } = await supabase
        .from('user_locations')
        .insert({
          user_id: user.user.id,
          profile_id: profile.id,
          latitude: location.latitude,
          longitude: location.longitude,
          accuracy: location.accuracy,
          heading: location.heading,
          speed: location.speed,
          is_active: true,
          last_updated: new Date().toISOString(),
        });

      if (error) {
        console.error('LocationTracker: Database insert error:', error);
        throw error;
      }
    }
    
    console.log('LocationTracker: Successfully updated user location in database');
  } catch (error) {
    console.error('LocationTracker: Error updating user location:', error);
    throw error;
  }
};

export const setLocationSharingForJob = async (
  jobId: string, 
  customerLocation: LocationData,
  customerAddress?: string
): Promise<void> => {
  try {
    console.log('LocationTracker: Setting location sharing for job:', jobId);
    
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) {
      console.error('LocationTracker: User not authenticated');
      throw new Error('Not authenticated');
    }
    console.log('LocationTracker: Authenticated user:', user.user.id);

    const { data: profile } = await supabase
      .from('profiles')
      .select('id, user_type')
      .eq('user_id', user.user.id)
      .maybeSingle();

    if (!profile) {
      console.error('LocationTracker: Profile not found for user:', user.user.id);
      throw new Error('Profile not found');
    }
    console.log('LocationTracker: Found profile:', profile);

    const { data: job } = await supabase
      .from('jobs')
      .select('tradesman_id, customer_id, status')
      .eq('id', jobId)
      .maybeSingle();

    if (!job) {
      console.error('LocationTracker: Job not found:', jobId);
      throw new Error('Job not found');
    }
    
    if (!job.tradesman_id) {
      console.error('LocationTracker: No tradesman assigned to job:', jobId);
      throw new Error('No tradesman assigned to this job yet');
    }
    
    console.log('LocationTracker: Job details:', job);

    // Verify the user is the customer for this job
    if (job.customer_id !== profile.id) {
      console.error('LocationTracker: User is not customer for this job', {
        profileId: profile.id,
        jobCustomerId: job.customer_id
      });
      throw new Error('You are not the customer for this job');
    }

    console.log('LocationTracker: Checking for existing location sharing...');
    // Check if location sharing record exists
    const { data: existingSharing } = await supabase
      .from('job_location_sharing')
      .select('id')
      .eq('job_id', jobId)
      .maybeSingle();

    if (existingSharing) {
      // Update existing sharing record
      console.log('LocationTracker: Updating existing location sharing record...');
      const { data: updatedData, error } = await supabase
        .from('job_location_sharing')
        .update({
          customer_latitude: customerLocation.latitude,
          customer_longitude: customerLocation.longitude,
          customer_address: customerAddress,
          tradesman_id: job.tradesman_id, // ensure correct tradesman is set for RLS visibility
          is_location_shared: true,
          sharing_started_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('job_id', jobId)
        .select();

      if (error) {
        console.error('LocationTracker: Database update error:', error);
        throw error;
      }
      console.log('LocationTracker: Successfully updated location sharing:', updatedData);
    } else {
      // Insert new sharing record
      console.log('LocationTracker: Creating new location sharing record...');
      const { data: insertedData, error } = await supabase
        .from('job_location_sharing')
        .insert({
          job_id: jobId,
          customer_id: profile.id,
          tradesman_id: job.tradesman_id,
          customer_latitude: customerLocation.latitude,
          customer_longitude: customerLocation.longitude,
          customer_address: customerAddress,
          is_location_shared: true,
          sharing_started_at: new Date().toISOString(),
        })
        .select();

      if (error) {
        console.error('LocationTracker: Database insert error:', error);
        throw error;
      }
      console.log('LocationTracker: Successfully created location sharing:', insertedData);
    }
  } catch (error) {
    console.error('LocationTracker: Error setting location sharing:', error);
    throw error;
  }
};

export const updateJobLocation = async (
  jobId: string,
  location: LocationData
): Promise<void> => {
  try {
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) throw new Error('Not authenticated');

    const { data: profile } = await supabase
      .from('profiles')
      .select('id')
      .eq('user_id', user.user.id)
      .maybeSingle();

    if (!profile) throw new Error('Profile not found');

    console.log('Updating job location:', {
      jobId,
      customerId: profile.id,
      location
    });

    // Do not overwrite jobs.location with raw coordinates.
    // Location will be shared via job_location_sharing; keep existing job address intact.
    console.log('Skipping jobs.location update to preserve human-readable address');

    // Kept existing job.location unchanged.
  } catch (error) {
    console.error('Error updating job location:', error);
    throw error;
  }
};